import React, { useEffect, useRef, useState } from 'react';
import { onClickPageButtonTracking, usePageTracking, useScrollTracking } from '../../hooks/pageTracking';
import { DeepLinkDefaultLink } from '../../utils/deepLinkUtils';
import { PauseSVG, PlaySVG, VolumeOffSVG, VolumeOnSVG } from '../../assets';
import { userAnalytics } from '../../utils/analytics/userAnalytics';
import { LandingButtonClickActionType, UserActionEnum } from '../../types/customLogger';
import LandingFooter from './LandingFooter';

const FLOATING_BUTTON_DEFAULT_BOTTOM = 15;
const FLOATING_BUTTON_WITH_AUDIO_BOTTOM = 108;
const LANDING_WRAP_AUDIO_PADDING_BOTTOM = 'calc(150px + env(safe-area-inset-bottom))';

type LandingImageCtaRenderProps = {
  deepLinkUri: string;
  buttonLabel: string;
  onTrackButtonClick: (label?: string) => void;
  floatingButtonStyle?: React.CSSProperties;
};

type LandingImageCtaPageProps = {
  pageName: string;
  buttonLabel: string;
  imageSrc: string;
  imageAlt?: string;
  showImageLoadingIndicator?: boolean;
  imageLoadingMessage?: string;
  deepLinkUri?: string;
  backgroundAudioSrc?: string;
  audioTitle?: string;
  autoPlayAudio?: boolean;
  useFloatingButton?: boolean;
  renderFloatingButton?: (props: LandingImageCtaRenderProps) => React.ReactNode;
  renderExtraContent?: (props: LandingImageCtaRenderProps) => React.ReactNode;
};

export default function LandingImageCtaPage({
  pageName,
  buttonLabel,
  imageSrc,
  imageAlt = '상세페이지 이미지',
  showImageLoadingIndicator = false,
  imageLoadingMessage = '이미지를 불러오는 중입니다...',
  deepLinkUri = DeepLinkDefaultLink,
  backgroundAudioSrc,
  audioTitle = 'BGM',
  autoPlayAudio = false,
  useFloatingButton = false,
  renderFloatingButton,
  renderExtraContent,
}: LandingImageCtaPageProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const heroImageRef = useRef<HTMLImageElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(true);

  usePageTracking(pageName);
  useScrollTracking(scrollRef, pageName);

  useEffect(() => {
    if (!imageSrc || typeof document === 'undefined') {
      return;
    }

    const preloadSelector = `link[rel="preload"][as="image"][href="${imageSrc}"]`;
    const existingPreloadLink = document.head.querySelector<HTMLLinkElement>(preloadSelector);
    if (existingPreloadLink) {
      existingPreloadLink.setAttribute('fetchpriority', 'high');
      return;
    }

    const preloadLink = document.createElement('link');
    preloadLink.rel = 'preload';
    preloadLink.as = 'image';
    preloadLink.href = imageSrc;
    preloadLink.setAttribute('fetchpriority', 'high');
    document.head.appendChild(preloadLink);
  }, [imageSrc]);

  useEffect(() => {
    const heroImage = heroImageRef.current;
    if (!heroImage) {
      return;
    }

    heroImage.loading = 'eager';
    heroImage.decoding = 'sync';
    heroImage.setAttribute('fetchpriority', 'high');
  }, [imageSrc]);

  useEffect(() => {
    if (!showImageLoadingIndicator) {
      setIsImageLoading(false);
      return;
    }

    const heroImage = heroImageRef.current;
    if (!heroImage) {
      return;
    }

    let isCanceled = false;
    const markImageLoaded = () => {
      if (isCanceled) {
        return;
      }
      setIsImageLoading(false);
    };

    setIsImageLoading(true);

    // Cached images can skip React onLoad timing, so we resolve loading state explicitly.
    if (heroImage.complete) {
      markImageLoaded();
      return;
    }

    heroImage.addEventListener('load', markImageLoaded);
    heroImage.addEventListener('error', markImageLoaded);

    return () => {
      isCanceled = true;
      heroImage.removeEventListener('load', markImageLoaded);
      heroImage.removeEventListener('error', markImageLoaded);
    };
  }, [imageSrc, showImageLoadingIndicator]);

  useEffect(() => {
    if (!backgroundAudioSrc) {
      return;
    }

    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    audio.loop = true;
    audio.muted = isMuted;
  }, [backgroundAudioSrc, isMuted]);

  useEffect(() => {
    if (!backgroundAudioSrc || !autoPlayAudio) {
      return;
    }

    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    let isCanceled = false;

    const onPlaySucceeded = () => {
      if (isCanceled) {
        return;
      }
      setIsPlaying(true);
      removeGestureListeners();
    };

    const tryPlayAudio = (withWarning = false) => {
      audio
        .play()
        .then(onPlaySucceeded)
        .catch((error) => {
          if (isCanceled) {
            return;
          }
          setIsPlaying(false);
          if (withWarning) {
            console.warn('[Failed] LandingImageCtaPage audio autoplay:', error);
          }
        });
    };

    const retryOnFirstGesture = () => {
      tryPlayAudio(false);
    };

    const removeGestureListeners = () => {
      window.removeEventListener('pointerdown', retryOnFirstGesture);
      window.removeEventListener('touchstart', retryOnFirstGesture);
      window.removeEventListener('keydown', retryOnFirstGesture);
      window.removeEventListener('wheel', retryOnFirstGesture);
      window.removeEventListener('scroll', retryOnFirstGesture);
    };

    window.addEventListener('pointerdown', retryOnFirstGesture, { passive: true });
    window.addEventListener('touchstart', retryOnFirstGesture, { passive: true });
    window.addEventListener('keydown', retryOnFirstGesture);
    window.addEventListener('wheel', retryOnFirstGesture, { passive: true });
    window.addEventListener('scroll', retryOnFirstGesture, { passive: true });

    tryPlayAudio(true);

    return () => {
      isCanceled = true;
      removeGestureListeners();
    };
  }, [autoPlayAudio, backgroundAudioSrc]);

  const onTrackButtonClick = (label = buttonLabel) => {
    onClickPageButtonTracking(pageName, label);
    const action = `${UserActionEnum.LANDING_BUTTON_CLICK}_${label}` as LandingButtonClickActionType;
    userAnalytics(action, pageName);
  };

  const toggleAudioPlay = () => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    if (audio.paused) {
      audio
        .play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch((error) => {
          console.warn('[Failed] LandingImageCtaPage audio play:', error);
        });
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  };

  const toggleAudioMute = () => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    const nextMuted = !audio.muted;
    audio.muted = nextMuted;
    setIsMuted(nextMuted);
  };

  const onSeekAudio = (event: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    const nextTime = Number(event.target.value);
    audio.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const formatAudioTime = (seconds: number) => {
    if (!Number.isFinite(seconds) || seconds < 0) {
      return '00:00';
    }

    const totalSeconds = Math.floor(seconds);
    const minute = Math.floor(totalSeconds / 60)
      .toString()
      .padStart(2, '0');
    const second = (totalSeconds % 60).toString().padStart(2, '0');
    return `${minute}:${second}`;
  };

  const floatingButtonStyle = useFloatingButton
    ? {
        bottom: backgroundAudioSrc ? FLOATING_BUTTON_WITH_AUDIO_BOTTOM : FLOATING_BUTTON_DEFAULT_BOTTOM,
      }
    : undefined;

  const landingWrapStyle = backgroundAudioSrc
    ? {
        paddingBottom: LANDING_WRAP_AUDIO_PADDING_BOTTOM,
      }
    : undefined;

  const renderProps = {
    deepLinkUri,
    buttonLabel,
    onTrackButtonClick,
    floatingButtonStyle,
  };

  return (
    <div ref={scrollRef} className="landing-wrap" style={landingWrapStyle}>
      {showImageLoadingIndicator && isImageLoading ? (
        <div className="landing-image-loading-message" role="status" aria-live="polite">
          {imageLoadingMessage}
        </div>
      ) : null}
      <img
        ref={heroImageRef}
        src={imageSrc}
        alt={imageAlt}
        loading="eager"
        onLoad={() => setIsImageLoading(false)}
        onError={() => setIsImageLoading(false)}
        style={{
          width: '100%',
          maxWidth: '860px',
          display: 'block',
          visibility: showImageLoadingIndicator && isImageLoading ? 'hidden' : 'visible',
        }}
      />
      {renderFloatingButton ? (
        renderFloatingButton(renderProps)
      ) : (
        <a
          id={useFloatingButton ? 'floating-button' : undefined}
          className={useFloatingButton ? 'landing-floating-btn landing-floating-active-btn' : 'landing-floating-btn'}
          href={deepLinkUri}
          onClick={() => onTrackButtonClick()}
          style={floatingButtonStyle}
        >
          {buttonLabel}
        </a>
      )}
      {renderExtraContent?.(renderProps)}
      {backgroundAudioSrc ? (
        <>
          <audio
            ref={audioRef}
            src={backgroundAudioSrc}
            loop
            autoPlay={autoPlayAudio}
            playsInline
            preload={autoPlayAudio ? 'auto' : 'metadata'}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
            onLoadedMetadata={(event) => setDuration(event.currentTarget.duration || 0)}
          />
          <div className="landing-audio-player">
            <button
              type="button"
              className="landing-audio-player-button"
              onClick={toggleAudioPlay}
              aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
            >
              {isPlaying ? (
                <PauseSVG width={20} height={20} className="landing-audio-player-icon" />
              ) : (
                <PlaySVG width={20} height={20} className="landing-audio-player-icon" />
              )}
            </button>
            <div className="landing-audio-player-center">
              <div className="landing-audio-player-title">{audioTitle}</div>
              <input
                type="range"
                aria-label="오디오 재생 위치"
                className="landing-audio-player-seek"
                min={0}
                max={duration || 0}
                value={Math.min(currentTime, duration || 0)}
                onChange={onSeekAudio}
                disabled={duration <= 0}
              />
            </div>
            <div className="landing-audio-player-time">{`${formatAudioTime(currentTime)} / ${formatAudioTime(duration)}`}</div>
            <button
              type="button"
              className="landing-audio-player-button"
              onClick={toggleAudioMute}
              aria-label={isMuted ? 'Unmute audio' : 'Mute audio'}
            >
              {isMuted ? (
                <VolumeOffSVG width={20} height={20} className="landing-audio-player-icon" />
              ) : (
                <VolumeOnSVG width={20} height={20} className="landing-audio-player-icon" />
              )}
            </button>
          </div>
        </>
      ) : null}
      <LandingFooter />
    </div>
  );
}
